import React, { useState } from 'react';
import finn from '../assets/finn.png';

/**
 * O Finn — a capivara roxa de moletom.
 *
 * ── O que este componente é ──────────────────────────────────────────────
 *
 * O personagem de verdade, recortado da arte original: cabeça, orelhas,
 * focinho e o começo do capuz.
 *
 * ── O fundo NÃO era transparente ─────────────────────────────────────────
 *
 * O arquivo original não tinha canal alpha (`hasAlpha: no`): o quadriculado
 * cinza-e-branco estava PINTADO na imagem, do jeito que um visualizador
 * desenha para representar transparência. Passou despercebido no avatar
 * pequeno porque a cabeça cobre quase todo o quadro — e apareceu gritando na
 * arte de corpo inteiro, onde 65% da imagem é fundo.
 *
 * `scripts/limpar-fundo-png.py` desfaz isso: preenchimento a partir das
 * BORDAS, então só some o que está conectado à moldura. Branco de olho e do
 * logo do moletom ficam, porque não encostam na borda.
 *
 * ── Por que componente, e não `<img>` solto nos dois lugares ─────────────
 *
 * Porque ele aparece em dois: o modal da porta e o marcador da bolha. A
 * primeira versão foi um SVG desenhado à mão, e trocar pela arte de verdade
 * custou uma linha justamente porque estava aqui dentro. Fosse copiado nos
 * dois, seriam dois — e um deles ficaria para trás na próxima troca.
 *
 * ── Por que recortado na cabeça ──────────────────────────────────────────
 *
 * Ele é usado entre 30px e 40px. O Finn inteiro, sentado de moletom, nesse
 * tamanho vira uma mancha roxa: o que se lê é o rosto. O corpo continua na
 * arte original, para quando existir um lugar grande o bastante.
 *
 * ── A borda redonda ──────────────────────────────────────────────────────
 *
 * O roxo é o DELE (`8D6CFB`, da folha de personagem), e não o do tema. Pela
 * mesma razão que o desenho não muda de cor nos dez temas: um personagem que
 * troca de cor com a preferência do app deixa de ser um personagem. No tema
 * violeta os dois quase coincidem; no laranja a borda roxa lê como "a cor do
 * Finn", que é o que ela é.
 *
 * O recheio claro existe porque a arte tem fundo transparente. Sem ele o
 * círculo mostraria a superfície de trás e a borda pareceria solta, sem nada
 * dentro — e no modo escuro isso ficaria pior, com o Finn escuro sobre fundo
 * escuro dentro de um anel.
 *
 * `padding` com `border-box` encolhe a arte para dentro do círculo em vez de
 * deixar o raio cortá-la: a orelha e o focinho chegam perto da borda do
 * quadrado, e um corte circular comeria os dois.
 *
 * ── Por que em `src/` e não em `public/` ─────────────────────────────────
 *
 * Importado, o bundler dá ao arquivo um nome com hash. Trocar a arte invalida
 * o cache sozinho. Em `public/` o caminho seria fixo, e quem já tivesse
 * `/finn.png` guardado continuaria vendo a versão velha sem forma de saber.
 */

interface Props {
  /** Lado em pixels. O padrão serve ao balão do chat; o modal usa maior. */
  tamanho?: number;
  className?: string;
  /**
   * Quando existe, o avatar vira BOTÃO — e ganha nome acessível.
   *
   * Sem isto ele é decoração (`aria-hidden`), porque quem diz que é o Finn é
   * o nome escrito ao lado. Clicável, ele passa a ser um controle, e controle
   * sem nome não existe para quem usa leitor de tela.
   */
  onClick?: () => void;
}

const FinnAvatar: React.FC<Props> = ({ tamanho = 30, className, onClick }) => {
  /*
   * ── Quando a imagem não carrega (18/09) ────────────────────────────────
   *
   * Ela mandou o print: o Finn era o ÍCONE DE IMAGEM QUEBRADA no balão do
   * chat. A causa estava na Vercel — a reescrita pega-tudo devolvia o
   * `index.html` para qualquer arquivo inexistente, então a aba que ela tinha
   * aberta desde antes do deploy pedia o `.png` do hash antigo e recebia HTML
   * com status 200. Isso foi consertado em `vercel.json`.
   *
   * Aqui fica o resto: com a aba velha, o arquivo REALMENTE não existe mais, e
   * nenhuma configuração traz ele de volta. O avatar então desenha o mesmo
   * círculo com a inicial dele. Continua sendo o Finn de 30px do lado do nome,
   * e não um papel rasgado.
   */
  const [falhou, setFalhou] = useState(false);

  const circulo: React.CSSProperties = {
    width: tamanho,
    height: tamanho,
    flex: '0 0 auto',
    borderRadius: '50%',
    border: '2px solid #8D6CFB',
    background: '#EDE6FF',
    boxSizing: 'border-box',
  };

  const arte = falhou ? (
    <span
      className={onClick ? undefined : className}
      aria-hidden="true"
      style={{
        ...circulo,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#5B3FD1',
        fontWeight: 800,
        fontSize: Math.round(tamanho * 0.5),
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      F
    </span>
  ) : (
    <img
      className={onClick ? undefined : className}
      src={finn}
    width={tamanho}
    height={tamanho}
    /*
     * `alt` vazio, e é o certo: quem diz que é o Finn é o nome escrito ao
     * lado, em texto de verdade. Um alt aqui faria o leitor de tela anunciar
     * "Finn" duas vezes seguidas.
     */
    alt=""
    aria-hidden="true"
    style={{
      ...circulo,
      /* A arte é quadrada; sem isto, um CSS de largura herdado a esticaria. */
      objectFit: 'contain',
      /* Ver a nota da borda: encolhe para dentro em vez de ser cortada. */
      padding: 2,
    }}
      draggable={false}
      onError={() => setFalhou(true)}
    />
  );

  if (!onClick) return arte;

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-label="Quem é o Finn"
      /*
       * O botão não pinta nada: ele é a moldura do avatar, e a borda redonda
       * já vive na imagem. Um fundo ou uma borda aqui somaria um segundo
       * círculo em volta do primeiro.
       */
      style={{
        padding: 0, border: 'none', background: 'none',
        lineHeight: 0, cursor: 'pointer', borderRadius: '50%', flex: '0 0 auto',
      }}
    >
      {arte}
    </button>
  );
};

export default FinnAvatar;
